/**
 * React Query hooks for Calendar Events
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { calendarEventsApi } from '@api/endpoints/calendarEvents'
import {
  CalendarEventDto,
  CalendarEventSearchParams,
  CreateCalendarEventRequest,
  UpdateCalendarEventRequest,
  SetRsvpRequest,
  RsvpStatus,
} from '@api/types/calendarEvent'

// ====================
// QUERY KEYS
// ====================

export const calendarEventKeys = {
  all: ['calendarEvents'] as const,
  lists: () => [...calendarEventKeys.all, 'list'] as const,
  list: (params?: CalendarEventSearchParams) => [...calendarEventKeys.lists(), params] as const,
  upcoming: (params?: { page?: number; pageSize?: number }) =>
    [...calendarEventKeys.all, 'upcoming', params] as const,
  myEvents: (params?: { page?: number; pageSize?: number; includeRsvps?: boolean }) =>
    [...calendarEventKeys.all, 'my-events', params] as const,
  byAdventure: (adventureId: string, params?: { page?: number; pageSize?: number }) =>
    [...calendarEventKeys.all, 'by-adventure', adventureId, params] as const,
  details: () => [...calendarEventKeys.all, 'detail'] as const,
  detail: (id: string) => [...calendarEventKeys.details(), id] as const,
  edits: () => [...calendarEventKeys.all, 'edit'] as const,
  edit: (id: string) => [...calendarEventKeys.edits(), id] as const,
  attendees: (id: string) => [...calendarEventKeys.all, 'attendees', id] as const,
  subscriptionLimits: () => [...calendarEventKeys.all, 'subscription-limits'] as const,
}

// ====================
// TRANSFORM HELPERS
// ====================

/**
 * Transform API response to match expected CalendarEventDto format
 */
function transformEvent(apiData: any): CalendarEventDto {
  return {
    id: apiData.id,
    title: apiData.title,
    quickDescription: apiData.quickDescription,
    fullDescription: apiData.fullDescription,
    status: apiData.status,
    startDateTime: apiData.startDateTime,
    endDateTime: apiData.endDateTime,
    timezone: apiData.timezone,
    isAllDay: apiData.isAllDay ?? false,
    location: apiData.location,
    isPublic: apiData.isPublic ?? true,
    maxAttendees: apiData.maxAttendees,
    owner: apiData.owner,
    creator: apiData.creator,
    coverImageUrl: apiData.coverImageUrl,
    coverImageId: apiData.coverImageId,
    tags: apiData.tags || [],
    linkedAdventure: apiData.linkedAdventure,
    attendanceStats: apiData.attendanceStats || {
      attendingCount: 0,
      tentativeCount: 0,
      notAttendingCount: 0,
      totalRsvpCount: 0,
    },
    userRsvp: apiData.userRsvp,
    isEventFull: apiData.isEventFull ?? false,
    createdDate: apiData.createdDate,
    modifiedDate: apiData.modifiedDate,
    canEdit: apiData.canEdit,
    canDelete: apiData.canDelete,
  }
}

/**
 * Handle various API response formats
 */
function handleApiResponse<T>(result: any, transform?: (data: any) => T): T {
  // Handle wrapped response (ApiResponse<T>)
  if (result && typeof result === 'object' && 'data' in result && result.data !== undefined) {
    const data = result.data
    return transform ? transform(data) : data
  }
  return transform ? transform(result) : result
}

/**
 * Handle paginated response
 */
function handlePaginatedResponse(result: any) {
  // Handle wrapped response
  if (result && typeof result === 'object' && 'data' in result) {
    result = result.data
  }

  // Transform items if present
  if (result && result.items && Array.isArray(result.items)) {
    return {
      ...result,
      items: result.items.map(transformEvent),
    }
  }

  // Handle 'events' field from search endpoint
  if (result && result.events && Array.isArray(result.events)) {
    return {
      items: result.events.map(transformEvent),
      totalCount: result.totalCount,
      pageNumber: result.page || result.pageNumber,
      pageSize: result.pageSize,
      totalPages: result.totalPages,
      hasNextPage: result.hasNextPage,
      hasPreviousPage: result.hasPreviousPage,
    }
  }

  return result
}

// ====================
// LIST QUERIES
// ====================

/**
 * Search calendar events
 */
export function useSearchCalendarEvents(params?: CalendarEventSearchParams) {
  return useQuery({
    queryKey: calendarEventKeys.list(params),
    queryFn: async () => {
      const result = await calendarEventsApi.searchEvents(params)
      return handlePaginatedResponse(result)
    },
    enabled: true,
  })
}

/**
 * Get upcoming public events
 */
export function useUpcomingEvents(params?: { page?: number; pageSize?: number }) {
  return useQuery({
    queryKey: calendarEventKeys.upcoming(params),
    queryFn: async () => {
      const result = await calendarEventsApi.getUpcomingEvents(params)
      return handlePaginatedResponse(result)
    },
  })
}

/**
 * Get user's events (created or RSVPed)
 */
export function useMyEvents(params?: { page?: number; pageSize?: number; includeRsvps?: boolean }) {
  return useQuery({
    queryKey: calendarEventKeys.myEvents(params),
    queryFn: async () => {
      const result = await calendarEventsApi.getMyEvents(params)
      return handlePaginatedResponse(result)
    },
  })
}

/**
 * Get events linked to an adventure
 */
export function useEventsByAdventure(
  adventureId: string,
  params?: { page?: number; pageSize?: number }
) {
  return useQuery({
    queryKey: calendarEventKeys.byAdventure(adventureId, params),
    queryFn: async () => {
      const result = await calendarEventsApi.getEventsByAdventure(adventureId, params)
      return handlePaginatedResponse(result)
    },
    enabled: !!adventureId,
  })
}

// ====================
// DETAIL QUERIES
// ====================

/**
 * Get single event by ID
 */
export function useCalendarEvent(id: string) {
  return useQuery({
    queryKey: calendarEventKeys.detail(id),
    queryFn: async () => {
      const result = await calendarEventsApi.getEvent(id)
      return handleApiResponse(result, transformEvent)
    },
    enabled: !!id,
  })
}

/**
 * Get event for editing
 */
export function useCalendarEventForEdit(id: string) {
  return useQuery({
    queryKey: calendarEventKeys.edit(id),
    queryFn: async () => {
      const result = await calendarEventsApi.getEventForEdit(id)
      return handleApiResponse(result, transformEvent)
    },
    enabled: !!id,
  })
}

/**
 * Get event attendees
 */
export function useEventAttendees(
  eventId: string,
  params?: { page?: number; pageSize?: number; status?: RsvpStatus }
) {
  return useQuery({
    queryKey: calendarEventKeys.attendees(eventId),
    queryFn: async () => {
      const result = await calendarEventsApi.getAttendees(eventId, params)
      return handleApiResponse(result)
    },
    enabled: !!eventId,
  })
}

/**
 * Get subscription limits
 */
export function useEventSubscriptionLimits() {
  return useQuery({
    queryKey: calendarEventKeys.subscriptionLimits(),
    queryFn: async () => {
      const result = await calendarEventsApi.getSubscriptionLimits()
      return handleApiResponse(result)
    },
    staleTime: 1000 * 60 * 5, // 5 minutes
  })
}

// ====================
// MUTATIONS
// ====================

/**
 * Create new event
 */
export function useCreateCalendarEvent() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (data: CreateCalendarEventRequest) => calendarEventsApi.createEvent(data),
    onSuccess: (result) => {
      const event = handleApiResponse(result, transformEvent)
      // Update cache
      if (event?.id) {
        queryClient.setQueryData(calendarEventKeys.detail(event.id), event)
      }
      // Invalidate lists
      queryClient.invalidateQueries({ queryKey: calendarEventKeys.lists() })
      queryClient.invalidateQueries({ queryKey: calendarEventKeys.upcoming() })
      queryClient.invalidateQueries({ queryKey: calendarEventKeys.myEvents() })
      queryClient.invalidateQueries({ queryKey: calendarEventKeys.subscriptionLimits() })
      return event
    },
  })
}

/**
 * Update event
 */
export function useUpdateCalendarEvent() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: UpdateCalendarEventRequest }) =>
      calendarEventsApi.updateEvent(id, data),
    onSuccess: (result, variables) => {
      const event = handleApiResponse(result, transformEvent)
      // Update cache
      if (event) {
        queryClient.setQueryData(calendarEventKeys.detail(variables.id), event)
        queryClient.setQueryData(calendarEventKeys.edit(variables.id), event)
      }
      // Invalidate lists
      queryClient.invalidateQueries({ queryKey: calendarEventKeys.lists() })
      queryClient.invalidateQueries({ queryKey: calendarEventKeys.upcoming() })
      queryClient.invalidateQueries({ queryKey: calendarEventKeys.myEvents() })
      return event
    },
  })
}

/**
 * Delete event
 */
export function useDeleteCalendarEvent() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (id: string) => calendarEventsApi.deleteEvent(id),
    onSuccess: (_, id) => {
      // Remove from cache
      queryClient.removeQueries({ queryKey: calendarEventKeys.detail(id) })
      queryClient.removeQueries({ queryKey: calendarEventKeys.edit(id) })
      // Invalidate lists
      queryClient.invalidateQueries({ queryKey: calendarEventKeys.lists() })
      queryClient.invalidateQueries({ queryKey: calendarEventKeys.upcoming() })
      queryClient.invalidateQueries({ queryKey: calendarEventKeys.myEvents() })
      queryClient.invalidateQueries({ queryKey: calendarEventKeys.subscriptionLimits() })
    },
  })
}

/**
 * Publish event
 */
export function usePublishCalendarEvent() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (id: string) => calendarEventsApi.publishEvent(id),
    onSuccess: (result, id) => {
      const event = handleApiResponse(result, transformEvent)
      // Update cache
      if (event) {
        queryClient.setQueryData(calendarEventKeys.detail(id), event)
        queryClient.setQueryData(calendarEventKeys.edit(id), event)
      }
      // Invalidate lists
      queryClient.invalidateQueries({ queryKey: calendarEventKeys.lists() })
      queryClient.invalidateQueries({ queryKey: calendarEventKeys.upcoming() })
      queryClient.invalidateQueries({ queryKey: calendarEventKeys.myEvents() })
      return event
    },
  })
}

/**
 * Cancel event
 */
export function useCancelCalendarEvent() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ id, reason }: { id: string; reason?: string }) =>
      calendarEventsApi.cancelEvent(id, reason),
    onSuccess: (result, variables) => {
      const event = handleApiResponse(result, transformEvent)
      // Update cache
      if (event) {
        queryClient.setQueryData(calendarEventKeys.detail(variables.id), event)
        queryClient.setQueryData(calendarEventKeys.edit(variables.id), event)
      }
      // Invalidate lists
      queryClient.invalidateQueries({ queryKey: calendarEventKeys.lists() })
      queryClient.invalidateQueries({ queryKey: calendarEventKeys.upcoming() })
      queryClient.invalidateQueries({ queryKey: calendarEventKeys.myEvents() })
      return event
    },
  })
}

// ====================
// RSVP MUTATIONS
// ====================

/**
 * Set RSVP
 */
export function useRsvpToEvent() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ eventId, data }: { eventId: string; data: SetRsvpRequest }) =>
      calendarEventsApi.setRsvp(eventId, data),
    onSuccess: (_, variables) => {
      // Invalidate event detail to refresh attendance stats
      queryClient.invalidateQueries({ queryKey: calendarEventKeys.detail(variables.eventId) })
      queryClient.invalidateQueries({ queryKey: calendarEventKeys.attendees(variables.eventId) })
      // Invalidate my events since RSVP status changed
      queryClient.invalidateQueries({ queryKey: calendarEventKeys.myEvents() })
    },
  })
}

/**
 * Remove RSVP
 */
export function useRemoveRsvp() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (eventId: string) => calendarEventsApi.removeRsvp(eventId),
    onSuccess: (_, eventId) => {
      // Invalidate event detail to refresh attendance stats
      queryClient.invalidateQueries({ queryKey: calendarEventKeys.detail(eventId) })
      queryClient.invalidateQueries({ queryKey: calendarEventKeys.attendees(eventId) })
      // Invalidate my events since RSVP status changed
      queryClient.invalidateQueries({ queryKey: calendarEventKeys.myEvents() })
    },
  })
}

// ====================
// TAG MUTATIONS
// ====================

/**
 * Save event tags
 */
export function useSaveEventTags() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ eventId, tagIds }: { eventId: string; tagIds: string[] }) =>
      calendarEventsApi.saveTags(eventId, tagIds),
    onSuccess: (_, variables) => {
      // Invalidate event detail to refresh tags
      queryClient.invalidateQueries({ queryKey: calendarEventKeys.detail(variables.eventId) })
      queryClient.invalidateQueries({ queryKey: calendarEventKeys.edit(variables.eventId) })
    },
  })
}
